import {
	Axs,
	CIMS_TM,
	FanXchange,
	GameTime,
	SeatGeek,
	StubHub,
	StubHubInt,
	TicketEvolution,
	TicketNetwork,
	TicketsNow,
	TickPick,
	ViaGogo,
	VividSeats,
} from "@src/assets/martkeplace-icons";
import { extractMarketplaceData } from "@src/store/pricing/settingsSlice";
import React from "react";
import { MyTooltip } from "../tooltip/Tooltip";
import { toast } from "sonner";
import { EventRecord } from "@src/store/pricing/queryEventsSlice";
import { ApiProvider } from "@src/store/ApiProvider";
import { ExportDestination } from "@src/constants/constants";
import { TExportDestinations } from "@src/store/system/listExportDestinationsSlice";
import type { EventInventoryRecord } from "@src/store/pricing/queryEventInventoryAllSlice";
import { buildPortalAssetUrl } from "@src/utils/portalAssets.utils";

// TicketsNow (TM-family) destination IDs - gated by SSN consent.
const TM_EXPORT_DESTINATION_IDS = new Set<number>([106]);

// Cat_Type substrings that require explicit TM consent before sharing to TicketsNow.
// Substring match handles variations like "Sports & Entertainment", "Pro Sports", "Concert Music".
const TM_RESTRICTED_CAT_TYPE_TOKENS = ["SPORT", "CONCERT"];

const isTMMarketplace = (m: { Export_Destination_ID: number }): boolean => TM_EXPORT_DESTINATION_IDS.has(m.Export_Destination_ID);

const isTMRestrictedCategory = (catType: string | null | undefined): boolean => {
	if (!catType) return false;
	const upper = catType.trim().toUpperCase();
	return TM_RESTRICTED_CAT_TYPE_TOKENS.some((token) => upper.includes(token));
};

// Listings don't carry Cat_Type - fall back to the cached events list keyed by Event_ID.
const getCatTypeForListing = (
	listing: { Event_ID?: number; Cat_Type?: string },
	events: Array<{ Event_ID: number; Cat_Type: string }>,
): string | null => {
	if (listing.Cat_Type) return listing.Cat_Type;
	if (listing.Event_ID) return events.find((e) => e.Event_ID === listing.Event_ID)?.Cat_Type ?? null;
	return null;
};

export const isAnyListingInTMRestrictedCategory = (
	listings: Array<{ Event_ID?: number; Cat_Type?: string }>,
	events: Array<{ Event_ID: number; Cat_Type: string }>,
): boolean => listings.some((l) => isTMRestrictedCategory(getCatTypeForListing(l, events)));

// TicketsNow tile is blocked only when ALL three are true:
// (a) TM marketplace, (b) user has not consented, (c) listing(s) in a restricted category.
export const isMarketplaceBlockedByTMConsent = (
	m: { Export_Destination_ID: number; ConsentToShareSSNWithTM: boolean },
	inRestrictedCategory: boolean,
): boolean => isTMMarketplace(m) && !m.ConsentToShareSSNWithTM && inRestrictedCategory;

export const buildMarketplaceIconUrl = (destination: { Exchange_Name: string; Icon_Url?: string | null }): string => {
	if (!destination.Icon_Url) return "";
	return buildPortalAssetUrl(destination.Icon_Url);
};

export const parseExportDestinationIdCsv = (csv: string | null | undefined): Array<number> => {
	if (!csv) return [];
	return csv
		.split(",")
		.map((s) => Number(s.trim()))
		.filter((n) => Number.isFinite(n) && n > 0);
};

const hasLiveListing = (value: unknown): boolean => Number(value) > 0;

const hasStringListingId = (value: unknown): boolean => {
	if (typeof value !== "string") return hasLiveListing(value);
	const trimmed = value.trim();
	if (!trimmed) return false;
	const numeric = Number(trimmed);
	return Number.isFinite(numeric) ? numeric > 0 : true;
};

const ACTIVE_WHEN_SHARED_WITHOUT_LISTING_ID = new Set<number>([ExportDestination.GameTime]);
const STRING_LISTING_ID_IDS = new Set<number>([ExportDestination.GameTime]);

export const getLiveMarketplaceIds = (row: EventInventoryRecord): Array<number> =>
	allMarketplaces
		.filter((m) => {
			if (!m.key) return false;
			const value = row[m.key as keyof EventInventoryRecord];
			return STRING_LISTING_ID_IDS.has(m.id) ? hasStringListingId(value) : hasLiveListing(value);
		})
		.map((m) => m.id);

export const getActiveMarketplaceIds = (row: EventInventoryRecord): Array<number> => {
	const withListingIdColumn = new Set(allMarketplaces.filter((m) => m.key).map((m) => m.id));
	const sharedOnly = parseExportDestinationIdCsv(row.Export_Destination_ID_CSV).filter(
		(id) => !withListingIdColumn.has(id) || ACTIVE_WHEN_SHARED_WITHOUT_LISTING_ID.has(id),
	);
	return Array.from(new Set([...getLiveMarketplaceIds(row), ...sharedOnly]));
};

export const LINKED_DESTINATION_GROUPS: Array<Array<number>> = [[ExportDestination.StubHub, ExportDestination.ViaGoGo]];

const findLinkedGroup = (id: number): Array<number> | undefined => LINKED_DESTINATION_GROUPS.find((group) => group.includes(id));

export const toggleDestinationSelection = (selected: Array<number>, id: number): Array<number> => {
	const group = findLinkedGroup(id) ?? [id];
	const isSelected = group.some((memberId) => selected.includes(memberId));
	if (isSelected) return selected.filter((x) => !group.includes(x));
	const toAdd = group.filter((memberId) => !selected.includes(memberId));
	return [...selected, ...toAdd];
};

export const syncLinkedDestinations = (selected: Array<number>): Array<number> => {
	const set = new Set(selected);
	for (const group of LINKED_DESTINATION_GROUPS) {
		if (group.some((id) => set.has(id))) group.forEach((id) => set.add(id));
	}
	return [...set];
};

export type TMarketplaceUnit = {
	key: string;
	id: number; // representative id passed to the click handler (toggle expands it to the group)
	ids: Array<number>;
	label: string;
	destinations: Array<TExportDestinations>;
};

export const buildMarketplaceUnits = (
	destinations: Array<TExportDestinations>,
	catalog?: Array<TExportDestinations>,
): Array<TMarketplaceUnit> => {
	const byId = new Map(destinations.map((d) => [d.Export_Destination_ID, d]));
	const catalogById = catalog ? new Map(catalog.map((d) => [d.Export_Destination_ID, d])) : null;
	const consumed = new Set<number>();
	const units: Array<TMarketplaceUnit> = [];

	for (const destination of destinations) {
		if (consumed.has(destination.Export_Destination_ID)) continue;
		const group = findLinkedGroup(destination.Export_Destination_ID);

		if (group) {
			group.forEach((id) => consumed.add(id));
			const members = group
				.map((id) => byId.get(id) ?? catalogById?.get(id))
				.filter((d): d is TExportDestinations => Boolean(d));
			units.push({
				key: `group-${group[0]}`,
				id: group[0],
				ids: members.map((m) => m.Export_Destination_ID),
				label: members.map((m) => m.Exchange_Name).join(" / "),
				destinations: members,
			});
		} else {
			consumed.add(destination.Export_Destination_ID);
			units.push({
				key: `single-${destination.Export_Destination_ID}`,
				id: destination.Export_Destination_ID,
				ids: [destination.Export_Destination_ID],
				label: destination.Exchange_Name,
				destinations: [destination],
			});
		}
	}

	return units;
};

export const allMarketplaces = [
	{ icon: <StubHub />, name: "StubHub", id: 104, key: "StubHub_Listing_ID", isDisabled: false, disabledMessage: null },
	{
		icon: <StubHubInt />,
		name: "StubHub International",
		id: 126,
		key: "StubHubInt_Listing_ID",
		isDisabled: false,
		disabledMessage: null,
	},
	{
		icon: <TicketsNow />,
		name: "Tickets Now",
		id: 106,
		key: "TicketsNow_Listing_ID",
		isDisabled: false,
		disabledMessage: "Not opted in",
	},
	{ icon: <VividSeats />, name: "Vivid Seats", id: 115, key: "Vivid_Listing_ID", isDisabled: false, disabledMessage: null },
	{
		icon: <TicketNetwork />,
		name: "Ticket Network",
		id: 105,
		key: "TicketNetwork_Listing_ID",
		isDisabled: false,
		disabledMessage: null,
	},
	{ icon: <SeatGeek />, name: "Seat Geek", id: 117, key: "SeatGeek_Listing_ID", isDisabled: false, disabledMessage: null },
	{ icon: <Axs />, name: "AXS", id: 125, key: "AXS_Listing_ID", isDisabled: true, disabledMessage: null },
	{
		icon: <GameTime />,
		name: "Game Time",
		id: ExportDestination.GameTime,
		key: "GT_Listing_ID",
		isDisabled: false,
		disabledMessage: null,
	},
	{ icon: <TicketEvolution />, name: "Ticket Evolution", id: 116, key: "", isDisabled: false, disabledMessage: null },
	{ icon: <TickPick />, name: "Tick Pick", id: 118, key: "", isDisabled: false, disabledMessage: null },
	{ icon: <FanXchange />, name: "Fan X Change", id: 122, key: "", isDisabled: false, disabledMessage: null },
	{ icon: <ViaGogo />, name: "ViaGogo", id: 120, key: "", isDisabled: false, disabledMessage: null },
	{ icon: <div>GT</div>, name: "GoTickets", id: 127, key: "", isDisabled: false, disabledMessage: null },
	{
		icon: <CIMS_TM />,
		name: "CIMS",
		id: ExportDestination.Cims,
		key: "CIMS_Listing_ID",
		isDisabled: false,
		disabledMessage: null,
	},
];

// ViaGogo and CIMS ship as remote <img> tags, so they can't stand in for a remote icon that failed.
const REMOTE_BACKED_ICON_IDS = new Set<number>([ExportDestination.ViaGoGo, ExportDestination.Cims]);

export const getLocalMarketplaceIcon = (exportDestinationId: number): React.ReactNode => {
	if (REMOTE_BACKED_ICON_IDS.has(exportDestinationId)) return null;
	return allMarketplaces.find((m) => m.id === exportDestinationId)?.icon ?? null;
};

export const getMarketplaceInitials = (exchangeName: string): string =>
	(exchangeName || "?")
		.split(/[\s/_-]+/)
		.filter(Boolean)
		.slice(0, 2)
		.map((word) => word[0].toUpperCase())
		.join("");

export const populateMarketplaceIcons = (data: ReturnType<typeof extractMarketplaceData>) => {
	const activeIds = Object.entries(data)
		.map(([key, value]) => value && String(key))
		.filter((x) => x);

	return activeIds.map((x) => {
		const i = allMarketplaces.find((i) => i.key === x);
		if (!i) return null;
		const tooltipId = `mp-icons${data.Listing_ID}-${i.name.replace(" ", "-")}`;
		return (
			<React.Fragment key={i.id}>
				<MyTooltip id={tooltipId} popup={i.name} element={<span style={{ flexShrink: 0 }}>{i.icon}</span>} />
			</React.Fragment>
		);
	});
};

type TMarketplaceNavigationRedirectTo =
	| "SeatGeek_Event_ID"
	| "TicketNetwork_Event_ID"
	| "TickPick_Event_ID"
	| "StubHubInt_Event_ID"
	| "TicketsNow_Production_ID"
	| "Vivid_Production_ID"
	| "StubHub_Event_ID"
	| "Event_Headliner"
	| "CIMS_Listing_ID";

export const marketplaceExternalEventNavigation = async (
	event?: EventRecord | null,
	redirectTo?: TMarketplaceNavigationRedirectTo | undefined,
) => {
	if (!event || !redirectTo) {
		toast.warning("Not available at this moment");
		return;
	}

	const allUrls = {
		SeatGeek_Event_ID: "https://seatgeek.com/e/events/<ID>",
		TicketNetwork_Event_ID: "https://eventprostickets.com/tickets/<ID>",
		TickPick_Event_ID: "https://www.tickpick.com/buy-tickets/<ID>/",
		StubHubInt_Event_ID: "https://www.stubhub.ie/event/<ID>",
		TicketsNow_Production_ID: "https://www.ticketmaster.com/event/<ID>",
		Vivid_Production_ID: "https://www.vividseats.com/shop/viewTickets.shtml?productionId=<ID>",
		StubHub_Event_ID: "https://www.stubhub.com/event/<ID>",
		Event_Headliner: "https://portal-ui.stagefront.com/datavue?eventHeadliner=<HEADLINER>",
		CIMS_Listing_ID: "",
	};

	const link = allUrls[redirectTo];

	if (redirectTo === "Event_Headliner") {
		const headliner = event.Event_Headliner ?? "";
		const url = link.replace("<HEADLINER>", encodeURIComponent(String(headliner)));
		window.open(url, "_blank");
		return;
	}

	let id = redirectTo === "TickPick_Event_ID" ? event.TicketNetwork_Event_ID : event[redirectTo];

	if (redirectTo === "TicketsNow_Production_ID") {
		try {
			const response = await ApiProvider.default.get<{ TM_Event_ID: string }>(
				`/api/EventInventory/GetProduction?productionId=${event[redirectTo]}`,
			);

			if (response?.TM_Event_ID?.trim()) {
				id = response.TM_Event_ID.trim();
			} else {
				id = null;
			}
		} catch {
			id = null;
		}
	}

	if (!link || !id) {
		toast.warning("Not available at this moment");
		return;
	}

	const redirectLink = link?.replace("<ID>", String(id));
	window.open(redirectLink, "_blank");
};

export const getSplitsName = (num: number | null | undefined) => {
	const val = Number(num);

	switch (val) {
		case 2:
			return "Pairs";
		case 1:
			return "Any";
		case 0:
			return "None";
		case -1:
		default:
			return "Default";
	}
};
